import defaultActionHandler from './actions/actionHandler'

/**
 * Configuration options for the Logs feature.
 *
 * The SDK will log information about the operations it is performing. The
 *    amount of information will depend on how the Logs feature is configured.
 *
 * The format of logs can also be customized by providing a
 *    {@link logger.LogHandler LogHandler}. This function will receive a
 *    {@link logger.LogEntry LogEntry} which it can handle as it sees fit. By
 *    default, the SDK will log information to the console. For more
 *    information, see the {@link logger Logs feature} description.
 *
 * @public
 * @static
 * @name config.logs
 * @memberof config
 * @requires logs
 * @instance
 * @param {Object} logs Logs configs.
 * @param {string} [logs.logLevel='debug'] Log level to be set. See {@link logger.levels}.
 * @param {logger.LogHandler} [logs.handler] The function to receive log entries from the
 *    SDK. If not provided, a default handler will be used that logs entries
 *    to the console.
 * @param {Object|boolean} [logs.logActions=false] Options specifically for action logs when
 *    logLevel is at DEBUG+ levels. Set this to false to not output action logs.
 * @param {logger.LogHandler} [logs.logActions.handler] The function to receive action
 *    log entries from the SDK. If not provided, a default handler will be used
 *    that logs actions to the console.
 * @param {boolean} [logs.logActions.actionOnly=false] Only output information
 *    about the action itself. Omits the SDK context for when it occurred.
 * @param {boolean} [logs.logActions.collapsed=false] Whether logs should be
 *    minimized when initially output. The full log is still output and can be
 *    inspected on the console.
 * @param {boolean} [logs.logActions.diff=false] Include a diff of what SDK
 *    context was changed by the action.
 * @param {string}  [logs.logActions.level='debug'] Log level to be set
 *    on the action logs
 * @param {boolean} [logs.logActions.exposePayloads=true] Allow action payloads
 *    to be exposed in the logs, potentially displaying sensitive information.
 */
export const defaultOptions = {
  logLevel: 'debug',
  handler: undefined,
  logActions: false
}

export const partialDefaultLogActions = {
  handler: defaultActionHandler,
  actionOnly: false,
  collapsed: false,
  diff: false,
  level: 'debug',
  exposePayloads: true
}
/*
 * TODO: Figure out a way to work around this.
 * Can't use validation in logging because validation uses logging to output errors.
 * Circular dependency, have to refactor.
 * Code:
 ```javascript
// Parse and/or Validate
// import { enums, validation as v8n, parse } from '../common/validation'
const defaultValidation = v8n.schema({
  logLevel: enums(['silent', 'error', 'warn', 'info', 'debug']),
  handler: v8n.optional(v8n.function()),
  logActions: v8n.optional(
    v8n.passesAnyOf(
      v8n.schema({
        handler: v8n.optional(v8n.function()),
        actionOnly: v8n.boolean(),
        collapsed: v8n.boolean(),
        diff: v8n.boolean(),
        exposePayloads: v8n.boolean()
      }),
      // OR
      v8n.boolean()
    )
  )
})

export const parseLogConfig = parse('logger', defaultValidation)
```
*/
